import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
    ArrowRight,
    BellFill,
    CheckCircleFill,
    GeoAltFill,
    HeartFill,
    LightningCharge,
    PiggyBankFill,
    PersonCircle,
    Shop,
    Trash,
} from 'react-bootstrap-icons';

import AddressSelector from '../components/AddressSelector';
import Stores from '../config/stores';
import { StoreContext } from '../contexts/StoreContext';
import {
    applyPreferredStores,
    clearCurrentSession,
    getCurrentUserEmail,
    loadProfile,
    saveProfile,
} from '../utils/profileStorage';
import { findFavoriteDealMatches } from '../utils/favoriteDeals';
import api_url from '../config/api_url';

const BUDGET_PRESETS = [350, 500, 700, 1000];

const getStoreDisplayName = (storeKey) => (
    Stores[storeKey]?.name || storeKey
        .replace(/([a-z])([A-Z])/g, '$1 $2')
        .trim()
);

function Profile() {
    const navigate = useNavigate();
    const { setStores } = useContext(StoreContext);
    const email = getCurrentUserEmail();
    const initialProfile = useMemo(() => loadProfile(email), [email]);
    const [locationCriteria, setLocationCriteria] = useState({
        address: initialProfile.address,
        radius: initialProfile.radius,
        geoLocation: initialProfile.geoLocation,
    });
    const [preferredStoreKeys, setPreferredStoreKeys] = useState(initialProfile.preferredStoreKeys);
    const [weeklyBudget, setWeeklyBudget] = useState(initialProfile.weeklyBudget || '');
    const [allowAllNearbyStores, setAllowAllNearbyStores] = useState(initialProfile.allowAllNearbyStores);
    const [inventoryMode, setInventoryMode] = useState(initialProfile.inventoryMode);
    const [favoriteRecipes, setFavoriteRecipes] = useState(initialProfile.favoriteRecipes);
    const [favoriteDealAlertsEnabled, setFavoriteDealAlertsEnabled] = useState(initialProfile.favoriteDealAlertsEnabled);
    const [favoriteDealThreshold, setFavoriteDealThreshold] = useState(initialProfile.favoriteDealThreshold);
    const [browserDealNotificationsEnabled, setBrowserDealNotificationsEnabled] = useState(initialProfile.browserDealNotificationsEnabled);
    const [notificationPermissionMessage, setNotificationPermissionMessage] = useState('');
    const [weeklyDealsData, setWeeklyDealsData] = useState(null);
    const [saveStatus, setSaveStatus] = useState('idle');

    useEffect(() => {
        let isCurrent = true;

        fetch(api_url + 'api/recommendations/weekly')
            .then((response) => response.ok ? response.json() : null)
            .then((data) => {
                if (isCurrent) {
                    setWeeklyDealsData(data);
                }
            })
            .catch(() => {
                if (isCurrent) {
                    setWeeklyDealsData(null);
                }
            });

        return () => {
            isCurrent = false;
        };
    }, []);

    const handleLocationCriteriaChange = useCallback((criteria) => {
        setLocationCriteria(criteria);
        setSaveStatus('idle');
    }, []);

    const togglePreferredStore = (storeKey) => {
        setPreferredStoreKeys((currentStoreKeys) => currentStoreKeys.includes(storeKey)
            ? currentStoreKeys.filter((currentStoreKey) => currentStoreKey !== storeKey)
            : [...currentStoreKeys, storeKey]);
        setSaveStatus('idle');
    };

    const handleSubmit = (event) => {
        event.preventDefault();

        const savedProfile = saveProfile({
            ...locationCriteria,
            email,
            preferredStoreKeys,
            weeklyBudget,
            allowAllNearbyStores,
            inventoryMode,
            favoriteRecipes,
            favoriteDealAlertsEnabled,
            favoriteDealThreshold,
            browserDealNotificationsEnabled,
        }, email);

        setStores((currentStores) => applyPreferredStores(
            currentStores,
            savedProfile.preferredStoreKeys
        ));
        setWeeklyBudget(savedProfile.weeklyBudget || '');
        setSaveStatus('saved');
    };

    const handleLogout = () => {
        clearCurrentSession();
        navigate('/login');
    };

    const handleBrowserNotificationsChange = async (isEnabled) => {
        setSaveStatus('idle');
        setNotificationPermissionMessage('');

        if (!isEnabled) {
            setBrowserDealNotificationsEnabled(false);
            return;
        }

        if (typeof window.Notification === 'undefined') {
            setBrowserDealNotificationsEnabled(false);
            setNotificationPermissionMessage('Din browser understøtter ikke denne type notifikation. Tilbuddene vises stadig på profilen.');
            return;
        }

        const permission = window.Notification.permission === 'granted'
            ? 'granted'
            : await window.Notification.requestPermission();

        setBrowserDealNotificationsEnabled(permission === 'granted');
        if (permission !== 'granted') {
            setNotificationPermissionMessage('Browsernotifikationer blev ikke tilladt. Tilbuddene vises stadig på profilen.');
        }
    };

    if (!email) {
        return (
            <main className="profile-page profile-page-signed-out" aria-labelledby="profile-title">
                <section className="profile-auth-card">
                    <span className="profile-section-icon" aria-hidden="true"><PersonCircle /></span>
                    <span className="profile-kicker">Din Budgetvenlig-konto</span>
                    <h1 id="profile-title">Log ind for at gemme dine valg</h1>
                    <p>Din adresse, dine butikker og dit ugentlige madbudget gemmes på din profil.</p>
                    <Link to="/login" className="profile-primary-action">
                        Log ind <ArrowRight aria-hidden="true" />
                    </Link>
                </section>
            </main>
        );
    }

    const budgetNumber = Number(weeklyBudget);
    const hasBudget = Number.isFinite(budgetNumber) && budgetNumber > 0;
    const favoriteDealMatches = findFavoriteDealMatches(weeklyDealsData, {
        favoriteRecipes,
        favoriteDealThreshold,
        preferredStoreKeys,
    });

    return (
        <main className="profile-page" aria-labelledby="profile-title">
            <header className="profile-hero">
                <div>
                    <span className="profile-kicker">Din Budgetvenlig-konto</span>
                    <h1 id="profile-title">Gør næste madplan lettere</h1>
                    <p>
                        Gem de valg, du normalt gentager. Vi bruger dem som udgangspunkt, og du kan altid ændre dem på forsiden.
                    </p>
                </div>
                <div className="profile-account-chip" title={email}>
                    <PersonCircle aria-hidden="true" />
                    <span><small>Logget ind som</small><strong>{email}</strong></span>
                </div>
            </header>

            <form className="profile-form" onSubmit={handleSubmit}>
                <section className="profile-section profile-location-section" aria-labelledby="profile-location-title">
                    <div className="profile-section-heading">
                        <span className="profile-section-icon" aria-hidden="true"><GeoAltFill /></span>
                        <div>
                            <span className="profile-section-step">1 · Dit område</span>
                            <h2 id="profile-location-title">Adresse og søgeradius</h2>
                            <p>Bruges til at finde de butikker, der faktisk ligger tæt på dig.</p>
                        </div>
                    </div>
                    <AddressSelector
                        initialCriteria={initialProfile}
                        onCriteriaChange={handleLocationCriteriaChange}
                    />
                    <p className="profile-field-note">
                        Vælg adressen fra forslagene, så koordinaterne kan gemmes sammen med adressen.
                    </p>
                </section>

                <section className="profile-section" aria-labelledby="profile-stores-title">
                    <div className="profile-section-heading">
                        <span className="profile-section-icon" aria-hidden="true"><Shop /></span>
                        <div>
                            <span className="profile-section-step">2 · Dine butikker</span>
                            <h2 id="profile-stores-title">Butikker du vil høre om</h2>
                            <p>Vi starter med disse, når vi samler tilbud og foreslår en madplan.</p>
                        </div>
                    </div>
                    <div className="profile-store-grid" role="group" aria-label="Vælg foretrukne butikker">
                        {Object.keys(Stores).map((storeKey) => {
                            const isSelected = preferredStoreKeys.includes(storeKey);
                            const displayName = getStoreDisplayName(storeKey);

                            return (
                                <button
                                    key={storeKey}
                                    type="button"
                                    className={`profile-store-option${isSelected ? ' is-selected' : ''}`}
                                    onClick={() => togglePreferredStore(storeKey)}
                                    aria-pressed={isSelected}
                                >
                                    <img src={Stores[storeKey].logo} alt="" aria-hidden="true" />
                                    <span>{displayName}</span>
                                    <span className="profile-store-check" aria-hidden="true">
                                        {isSelected && <CheckCircleFill />}
                                    </span>
                                </button>
                            );
                        })}
                    </div>
                    <label className="profile-checkbox">
                        <input
                            type="checkbox"
                            checked={allowAllNearbyStores}
                            onChange={(event) => {
                                setAllowAllNearbyStores(event.target.checked);
                                setSaveStatus('idle');
                            }}
                        />
                        <span>
                            <strong>Brug gerne alle butikker i nærheden</strong>
                            <small>Hvis det hjælper med at holde mit ugentlige budget, må planen også bruge andre fundne butikker.</small>
                        </span>
                    </label>
                </section>

                <section className="profile-section profile-inventory-section" aria-labelledby="profile-inventory-title">
                    <div className="profile-section-heading">
                        <span className="profile-section-icon" aria-hidden="true"><LightningCharge /></span>
                        <div>
                            <span className="profile-section-step">3 · Dit spisekammer</span>
                            <h2 id="profile-inventory-title">Hvor meget vil du registrere?</h2>
                            <p>Vælg om Budgetvenlig skal estimere lageret for dig, eller om du selv vil bekræfte hver ændring.</p>
                        </div>
                    </div>
                    <div className="profile-inventory-options" role="radiogroup" aria-label="Vælg styring af spisekammer">
                        <button
                            type="button"
                            role="radio"
                            aria-checked={inventoryMode === 'smart'}
                            className={inventoryMode === 'smart' ? 'is-selected' : ''}
                            onClick={() => {
                                setInventoryMode('smart');
                                setSaveStatus('idle');
                            }}
                        >
                            <span className="profile-inventory-option-heading">
                                <LightningCharge aria-hidden="true" />
                                <strong>Smart</strong>
                                <small>Anbefalet</small>
                            </span>
                            <span>Du bekræfter kun, at hele listen er købt, og at en ret er lavet. Budgetvenlig beregner derefter pakker, forbrug og sandsynlige rester.</span>
                            <small className="profile-inventory-option-details">God hvis du følger indkøbslisten nogenlunde og helst vil undgå løbende registrering.</small>
                        </button>
                        <button
                            type="button"
                            role="radio"
                            aria-checked={inventoryMode === 'precise'}
                            className={inventoryMode === 'precise' ? 'is-selected' : ''}
                            onClick={() => {
                                setInventoryMode('precise');
                                setSaveStatus('idle');
                            }}
                        >
                            <span className="profile-inventory-option-heading">
                                <CheckCircleFill aria-hidden="true" />
                                <strong>Præcis</strong>
                            </span>
                            <span>Du markerer præcis, hvad du købte, og bekræfter selv, hvad der blev brugt eller gemt efter hver ret.</span>
                            <small className="profile-inventory-option-details">God hvis du ofte ændrer indkøbene eller vil have fuld kontrol over hver mængde.</small>
                        </button>
                    </div>
                    <p className="profile-field-note">
                        Smart-registreringer vises som estimater og kan altid rettes eller fjernes i Dit spisekammer.
                    </p>
                </section>

                <section className="profile-section profile-budget-section" aria-labelledby="profile-budget-title">
                    <div className="profile-section-heading">
                        <span className="profile-section-icon" aria-hidden="true"><PiggyBankFill /></span>
                        <div>
                            <span className="profile-section-step">4 · Din beløbsgrænse</span>
                            <h2 id="profile-budget-title">Ugentligt madbudget</h2>
                            <p>Sæt et loft for indkøbene til den madplan, vi bygger til dig.</p>
                        </div>
                    </div>
                    <div className="profile-budget-control">
                        <label htmlFor="profile-weekly-budget">
                            <span>Maksimalt pr. uge</span>
                            <span className="profile-budget-input-wrap">
                                <input
                                    id="profile-weekly-budget"
                                    type="number"
                                    min="50"
                                    max="5000"
                                    step="25"
                                    inputMode="numeric"
                                    value={weeklyBudget}
                                    onChange={(event) => {
                                        setWeeklyBudget(event.target.value);
                                        setSaveStatus('idle');
                                    }}
                                    placeholder="500"
                                />
                                <strong>kr.</strong>
                            </span>
                        </label>
                        <div className="profile-budget-presets" aria-label="Vælg et budgetforslag">
                            {BUDGET_PRESETS.map((amount) => (
                                <button
                                    key={amount}
                                    type="button"
                                    className={budgetNumber === amount ? 'is-selected' : ''}
                                    onClick={() => {
                                        setWeeklyBudget(amount);
                                        setSaveStatus('idle');
                                    }}
                                >
                                    {amount} kr.
                                </button>
                            ))}
                        </div>
                    </div>
                    <div className={`profile-budget-explainer${hasBudget ? ' has-budget' : ''}`}>
                        <PiggyBankFill aria-hidden="true" />
                        <p>
                            <strong>{hasBudget ? `Din grænse er ${Math.round(budgetNumber)} kr. om ugen` : 'Budgettet er valgfrit'}</strong>
                            <span>
                                {hasBudget
                                    ? 'Vi prioriterer billige retter og stopper, før den beregnede indkøbspris overskrider beløbet.'
                                    : 'Uden en grænse vælger vi primært efter værdi, tilbud og variation.'}
                            </span>
                        </p>
                    </div>
                </section>

                <section className="profile-section profile-favorites-section" aria-labelledby="profile-favorites-title">
                    <div className="profile-section-heading">
                        <span className="profile-section-icon" aria-hidden="true"><BellFill /></span>
                        <div>
                            <span className="profile-section-step">5 · Favoritter og tilbud</span>
                            <h2 id="profile-favorites-title">Giv besked, når favoritterne er på tilbud</h2>
                            <p>Vi holder øje med ingredienserne i dine favoritretter og din valgte rabatgrænse.</p>
                        </div>
                    </div>

                    {favoriteRecipes.length > 0 ? (
                        <div className="profile-favorite-list" aria-label="Dine favoritretter">
                            {favoriteRecipes.map((favorite) => (
                                <div className="profile-favorite-item" key={favorite.recipeId}>
                                    {favorite.image ? (
                                        <img src={favorite.image} alt="" aria-hidden="true" />
                                    ) : (
                                        <span className="profile-favorite-placeholder" aria-hidden="true"><HeartFill /></span>
                                    )}
                                    <Link to={`/recipes/${favorite.recipeId}`}>{favorite.title}</Link>
                                    <button
                                        type="button"
                                        onClick={() => {
                                            setFavoriteRecipes((currentFavorites) => currentFavorites.filter(
                                                (currentFavorite) => currentFavorite.recipeId !== favorite.recipeId
                                            ));
                                            setSaveStatus('idle');
                                        }}
                                        aria-label={`Fjern ${favorite.title} fra favoritter`}
                                    >
                                        <Trash aria-hidden="true" />
                                    </button>
                                </div>
                            ))}
                        </div>
                    ) : (
                        <div className="profile-favorites-empty">
                            <HeartFill aria-hidden="true" />
                            <p><strong>Du har ingen favoritter endnu</strong><span>Tryk på hjertet ved en opskrift for at gemme den her.</span></p>
                            <Link to="/recipes">Find opskrifter</Link>
                        </div>
                    )}

                    <label className="profile-checkbox profile-alert-master-toggle">
                        <input
                            type="checkbox"
                            checked={favoriteDealAlertsEnabled}
                            disabled={favoriteRecipes.length === 0}
                            onChange={(event) => {
                                setFavoriteDealAlertsEnabled(event.target.checked);
                                setSaveStatus('idle');
                            }}
                        />
                        <span>
                            <strong>Aktivér tilbudsbeskeder for favoritretter</strong>
                            <small>Tilbuddene vises på din profil og kan også vises som browsernotifikationer.</small>
                        </span>
                    </label>

                    <div className={`profile-alert-settings${favoriteDealAlertsEnabled ? ' is-enabled' : ''}`}>
                        <label htmlFor="profile-deal-threshold" className="profile-threshold-control">
                            <span>
                                <strong>Mindste rabat</strong>
                                <em>{favoriteDealThreshold}%</em>
                            </span>
                            <input
                                id="profile-deal-threshold"
                                type="range"
                                min="5"
                                max="90"
                                step="5"
                                value={favoriteDealThreshold}
                                disabled={!favoriteDealAlertsEnabled}
                                onChange={(event) => {
                                    setFavoriteDealThreshold(Number(event.target.value));
                                    setSaveStatus('idle');
                                }}
                            />
                            <small>
                                Giv besked, når mindst én ingrediens er sat {favoriteDealThreshold}% eller mere ned {preferredStoreKeys.length > 0 ? 'i dine valgte butikker' : 'blandt ugens tilbud'}.
                            </small>
                        </label>

                        <label className="profile-checkbox profile-browser-alert-toggle">
                            <input
                                type="checkbox"
                                checked={browserDealNotificationsEnabled}
                                disabled={!favoriteDealAlertsEnabled}
                                onChange={(event) => handleBrowserNotificationsChange(event.target.checked)}
                            />
                            <span>
                                <strong>Vis også en browsernotifikation</strong>
                                <small>Virker, når Budgetvenlig er åben. Baggrundsbeskeder kræver senere en serverbaseret push-løsning.</small>
                            </span>
                        </label>
                        {notificationPermissionMessage && (
                            <p className="profile-notification-message" role="status">{notificationPermissionMessage}</p>
                        )}
                    </div>

                    {favoriteDealAlertsEnabled && (
                        <div className="profile-current-deals" aria-live="polite">
                            <div className="profile-current-deals-heading">
                                <span>Matcher lige nu</span>
                                <strong>{favoriteDealMatches.reduce((total, match) => total + match.offers.length, 0)}</strong>
                            </div>
                            {favoriteDealMatches.length > 0 ? favoriteDealMatches.map((match) => (
                                <article key={match.recipeId} className="profile-deal-match">
                                    <Link to={`/recipes/${match.recipeId}`}>{match.title}</Link>
                                    <ul>
                                        {match.offers.slice(0, 4).map((offer) => (
                                            <li key={`${match.recipeId}-${offer.store}-${offer.ingredient}`}>
                                                <span>{offer.ingredient} · {offer.store}</span>
                                                <strong>-{Math.round(offer.discountPercentage)}%</strong>
                                            </li>
                                        ))}
                                    </ul>
                                </article>
                            )) : (
                                <p className="profile-no-deal-matches">
                                    Ingen favoritingredienser matcher {favoriteDealThreshold}% {preferredStoreKeys.length > 0 ? 'i dine valgte butikker' : 'blandt ugens tilbud'} denne uge.
                                </p>
                            )}
                        </div>
                    )}
                </section>

                <footer className="profile-actions">
                    <div className={`profile-save-status${saveStatus === 'saved' ? ' is-visible' : ''}`} role="status" aria-live="polite">
                        <CheckCircleFill aria-hidden="true" />
                        Dine profilvalg er gemt
                    </div>
                    <div className="profile-action-buttons">
                        <button type="button" className="profile-logout-button" onClick={handleLogout}>Log ud</button>
                        <button type="submit" className="profile-primary-action">
                            Gem profil <ArrowRight aria-hidden="true" />
                        </button>
                    </div>
                </footer>
            </form>
        </main>
    );
}

export default Profile;
